import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  IoAlertCircleOutline, IoAttachOutline, IoCheckmarkCircle, IoCloseCircleOutline, IoCreateOutline, IoMailOutline,
  IoSaveOutline, IoSendOutline, IoTimeOutline,
} from 'react-icons/io5';

import {
  confirmPendingAction, cancelPendingAction, getPendingAction, saveEmailEdits, unschedulePendingAction,
} from '../atlasApi';
import { useCardDecided } from './cardDecisions';

// Mirrors the backend's _EMAIL_RE so invalid chips are flagged before the send 400s.
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
const HTML_BODY_RE = /<(p|div|br|blockquote|table|a|span|img|ul|ol|li|h[1-6]|strong|em|b|i)\b/i;

function recipientLabel(list) {
  if (!Array.isArray(list)) return '';
  return list
    .map((r) => (typeof r === 'string' ? r : r?.email || ''))
    .filter(Boolean)
    .join(', ');
}

// preview.to ([{email}|string, ...]) -> ['a@x.com', ...] for the chip field.
function recipientsToEmails(list) {
  if (!Array.isArray(list)) return [];
  return list
    .map((r) => (typeof r === 'string' ? r : r?.email || ''))
    .map((s) => s.trim())
    .filter(Boolean);
}

// Scheduled send: the card shows and edits times in the viewer's own time zone; the
// backend stores UTC (send_at ISO) and Nylas holds the email until then.
const pad2 = (n) => String(n).padStart(2, '0');

// ISO -> 'YYYY-MM-DDTHH:mm' in local time, for <input type="datetime-local">.
export function toLocalInput(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}T${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

// 'Wed, Oct 21, 8:30 AM EDT' in the viewer's time zone.
export function formatSendAt(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString([], { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZoneName: 'short' });
}

// Mailboxes that can't send right now (an expired connection): named on the card,
// never offered as From.
const unavailableMailboxes = (preview) => (Array.isArray(preview?.from_unavailable)
  ? preview.from_unavailable.filter((u) => u && u.email)
  : []);

// "See attached", "the attachment", "enclosed": the message promises a file.
const ATTACH_MENTION_RE = /\b(attach(ed|es|ing|ment|ments)?|enclosed)\b/i;

export function formatBytes(n) {
  const v = Number(n);
  if (!Number.isFinite(v) || v <= 0) return '';
  if (v < 1024) return `${v} B`;
  if (v < 1024 * 1024) return `${Math.round(v / 1024)} KB`;
  return `${(v / (1024 * 1024)).toFixed(v < 10 * 1024 * 1024 ? 1 : 0)} MB`;
}

// What goes out with the email: name + size when the backend sent details, else names.
export function attachmentItems(preview) {
  const details = Array.isArray(preview?.attachment_details)
    ? preview.attachment_details.filter((a) => a && a.name)
    : [];
  if (details.length) return details;
  return (Array.isArray(preview?.attachments) ? preview.attachments : [])
    .filter(Boolean)
    .map((name) => ({ name: String(name) }));
}

function AttachmentList({ items }) {
  return (
    <span className="atlas-email-attachments">
      {items.map((a, i) => (
        <span
          key={`${a.name}-${i}`}
          className="atlas-email-attachment"
          title={a.source === 'forward' ? `${a.name}, from the forwarded email` : a.name}
        >
          <IoAttachOutline size={14} aria-hidden="true" />
          <span className="atlas-email-attachment-name">{a.name}</span>
          {formatBytes(a.size_bytes) && <span className="atlas-email-attachment-size">{formatBytes(a.size_bytes)}</span>}
        </span>
      ))}
    </span>
  );
}

// Add comma/semicolon/space-separated token(s) to an address list, de-duped (case-insensitive).
function addTokens(list, raw) {
  const seen = new Set(list.map((v) => v.toLowerCase()));
  const next = [...list];
  for (const t of String(raw).split(/[,;\s]+/).map((s) => s.trim()).filter(Boolean)) {
    if (!seen.has(t.toLowerCase())) {
      next.push(t);
      seen.add(t.toLowerCase());
    }
  }
  return next;
}

/**
 * Chip/token recipient input. Type an address + Enter / comma / semicolon (or paste a
 * list) to add a pill; ✕ or Backspace (on empty input) removes one. Malformed addresses
 * render red. State is a plain array of email strings plus the in-progress input `text`,
 * both owned by the parent so an uncommitted address is never lost on Approve.
 */
function RecipientChips({ value, text, onChange, onTextChange, placeholder, disabled }) {
  const inputRef = useRef(null);
  const commit = (raw) => {
    onChange(addTokens(value, raw));
    onTextChange('');
  };
  const onKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',' || e.key === ';') {
      if (text.trim()) {
        e.preventDefault();
        commit(text);
      } else if (e.key !== 'Enter') {
        e.preventDefault();
      }
    } else if (e.key === 'Backspace' && !text && value.length) {
      onChange(value.slice(0, -1));
    }
  };
  const onPaste = (e) => {
    const pasted = e.clipboardData.getData('text');
    if (/[,;\s]/.test(pasted)) {
      e.preventDefault();
      commit(text ? `${text} ${pasted}` : pasted);
    }
  };
  return (
    <div
      className={`atlas-chips${disabled ? ' atlas-chips-disabled' : ''}`}
      onClick={() => inputRef.current && inputRef.current.focus()}
    >
      {value.map((email, i) => (
        <span key={`${email}-${i}`} className={`atlas-chip${EMAIL_RE.test(email) ? '' : ' atlas-chip-invalid'}`}>
          <span className="atlas-chip-label">{email}</span>
          <button
            type="button"
            className="atlas-chip-x"
            disabled={disabled}
            onClick={(e) => { e.stopPropagation(); onChange(value.filter((_, idx) => idx !== i)); }}
            aria-label={`Remove ${email}`}
          >
            ×
          </button>
        </span>
      ))}
      <input
        ref={inputRef}
        type="text"
        className="atlas-chips-input"
        value={text}
        onChange={(e) => onTextChange(e.target.value)}
        onKeyDown={onKeyDown}
        onPaste={onPaste}
        onBlur={() => text.trim() && commit(text)}
        placeholder={value.length ? '' : placeholder}
        disabled={disabled}
      />
    </div>
  );
}

/**
 * Approval card for a staged email send. Nothing is created in the user's mailbox
 * at stage time — the email is composed and sent directly (Nylas messages/send,
 * straight to Sent) only when the user clicks Approve here (which hits
 * /v1/pending-actions/{id}/confirm). The approver can Edit the recipient, subject,
 * and copy, then Save edits (stored on the pending email, nothing sent) or send with
 * the edits riding along on the confirm call. Attached files are listed by name and
 * size in every state; editing never changes them. With more than
 * one connected mailbox, From shows the address Atlas picked (and why) and the user can
 * switch it for a new email; replies and forwards stay in the original's mailbox. Cancel just
 * discards the staged send; there is no draft to clean up. A transient 'sending'
 * status is shown while the send is in flight.
 */
export default function EmailApprovalCard({ approval }) {
  const onDecided = useCardDecided();
  const actionId = approval?.actionId;
  const [status, setStatus] = useState(approval?.status || 'pending');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  // The streamed event only carries {to, subject} to keep the model's context
  // light; the full preview (incl. full body + cc) is fetched from the server on
  // mount. `loaded` gates editing so we never seed the form from a partial preview.
  const [preview, setPreview] = useState(approval?.preview || {});
  const [loaded, setLoaded] = useState(() => typeof approval?.preview?.body === 'string');

  // Edit mode + working copies of the editable fields. Recipients are chip lists
  // (arrays of emails) plus the not-yet-committed input text for each field.
  const [editing, setEditing] = useState(false);
  const [editToList, setEditToList] = useState([]);
  const [editToText, setEditToText] = useState('');
  const [editCcList, setEditCcList] = useState([]);
  const [editCcText, setEditCcText] = useState('');
  const [editSubject, setEditSubject] = useState('');
  const [editBody, setEditBody] = useState('');
  const [savedNote, setSavedNote] = useState('');
  // From: the mailbox Atlas picked; the user may switch it (new emails only).
  const [fromAccount, setFromAccount] = useState(approval?.preview?.from || '');
  // When: '' sends on approval; a local datetime schedules it. scheduledFor is the
  // time Nylas actually holds it for, once approved.
  const [sendAt, setSendAt] = useState(() => toLocalInput(approval?.preview?.send_at));
  const [pickingTime, setPickingTime] = useState(false);
  const [scheduledFor, setScheduledFor] = useState(approval?.result?.send_at || null);
  useEffect(() => {
    setSendAt(toLocalInput(preview.send_at));
  }, [preview.send_at]);
  useEffect(() => {
    // The From mailbox can't send (its connection expired): pick one that can.
    const bad = new Set(unavailableMailboxes(preview).map((u) => u.email.toLowerCase()));
    if (preview.from && bad.has(preview.from.toLowerCase())) {
      const choices = Array.isArray(preview.from_choices) ? preview.from_choices : [];
      const working = choices.find((c) => c && !bad.has(String(c).toLowerCase()));
      if (working) {
        setFromAccount(working);
        return;
      }
    }
    if (preview.from) setFromAccount(preview.from);
  }, [preview]);

  const reload = useCallback(async () => {
    const data = await getPendingAction({ actionId });
    if (data?.preview && typeof data.preview === 'object') setPreview(data.preview);
    if (data?.status) setStatus(data.status);
    return data;
  }, [actionId]);

  useEffect(() => {
    let cancelled = false;
    if (!actionId) return undefined;
    getPendingAction({ actionId })
      .then((data) => {
        if (cancelled) return;
        if (data?.preview && typeof data.preview === 'object') setPreview(data.preview);
        if (data?.status && data.status !== 'pending') setStatus(data.status);
        if (data?.result?.send_at) setScheduledFor(data.result.send_at);
        setLoaded(true);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [actionId]);

  const enterEdit = useCallback(() => {
    setEditToList(recipientsToEmails(preview.to));
    setEditToText('');
    setEditCcList(recipientsToEmails(preview.cc));
    setEditCcText('');
    setEditSubject(preview.subject || '');
    setEditBody(preview.body || '');
    setError('');
    setSavedNote('');
    setEditing(true);
  }, [preview]);

  const discardEdit = useCallback(() => {
    setEditing(false);
    setError('');
  }, []);

  // Only fields the user actually CHANGED, so an untouched (or not-yet-loaded) field
  // never overwrites the real staged value server-side. Flushes any typed-but-
  // uncommitted address so it isn't lost.
  const collectEdits = useCallback(() => {
    let toList = editToList;
    let ccList = editCcList;
    let edits;
    if (editing) {
      toList = addTokens(editToList, editToText);
      ccList = addTokens(editCcList, editCcText);
      edits = {};
      if (toList.join('\n') !== recipientsToEmails(preview.to).join('\n')) edits.to = toList;
      if (ccList.join('\n') !== recipientsToEmails(preview.cc).join('\n')) edits.cc = ccList;
      if (editSubject !== (preview.subject || '')) edits.subject = editSubject;
      if (editBody !== (preview.body || '')) edits.body = editBody;
    }
    if (fromAccount && preview.from && fromAccount !== preview.from) {
      edits = { ...(edits || {}), from_account: fromAccount };
    }
    if (sendAt !== toLocalInput(preview.send_at)) {
      edits = { ...(edits || {}), send_at: sendAt ? new Date(sendAt).toISOString() : null };
    }
    return { edits, toList, ccList };
  }, [editing, editToList, editToText, editCcList, editCcText, editSubject, editBody, preview, fromAccount, sendAt]);

  const onSaveEdits = useCallback(async () => {
    if (!actionId || busy) return;
    const { edits } = collectEdits();
    if (!edits || !Object.keys(edits).length) {
      setEditing(false);
      return;
    }
    setBusy(true);
    setError('');
    try {
      const res = await saveEmailEdits({ actionId, edits });
      if (res?.preview && typeof res.preview === 'object') setPreview(res.preview);
      setEditing(false);
      setSavedNote('Edits saved. Approve & send sends this version.');
    } catch (e) {
      setError(e?.message || 'Could not save the edits.');
    } finally {
      setBusy(false);
    }
  }, [actionId, busy, collectEdits]);

  const onApprove = useCallback(async () => {
    if (!actionId || busy) return;
    setBusy(true);
    setError('');
    try {
      const { edits, toList, ccList } = collectEdits();
      const res = await confirmPendingAction({ actionId, edits });
      if (res?.result?.send_at) setScheduledFor(res.result.send_at);
      if (fromAccount && fromAccount !== preview.from) {
        setPreview((p) => ({ ...p, from: fromAccount }));
      }
      if (editing) {
        // Reflect the edits locally so the resolved card shows what actually went out.
        setPreview((p) => ({
          ...p,
          to: toList.map((email) => ({ email })),
          cc: ccList.map((email) => ({ email })),
          subject: editSubject,
          body: editBody,
          body_truncated: false,
        }));
        setEditing(false);
      }
      setStatus(res?.status || 'sent');
      onDecided?.({ actionId, kind: 'email_send', status: res?.status || 'sent' });
    } catch (e) {
      // Backend restores the action to 'pending' on a bad edit (400) so the user can fix it.
      // 409: the mailbox refused it (e.g. an expired connection) and nothing went out. The
      // card stays sendable: the server kept the edits and dropped that mailbox from From.
      if (e?.status === 409) {
        try {
          await reload();
          setEditing(false);
        } catch {
          // keep the card as is; the message below still explains
        }
      }
      setError(e?.message || 'Could not send the email. Please try again.');
    } finally {
      setBusy(false);
    }
  }, [actionId, busy, editing, editSubject, editBody, preview, fromAccount, collectEdits, onDecided, reload]);

  const onUnschedule = useCallback(async () => {
    if (!actionId || busy) return;
    setBusy(true);
    setError('');
    try {
      const res = await unschedulePendingAction({ actionId });
      setStatus(res?.status || 'cancelled');
    } catch (e) {
      setError(e?.message || 'Could not cancel the scheduled email.');
    } finally {
      setBusy(false);
    }
  }, [actionId, busy]);

  const onCancel = useCallback(async () => {
    if (!actionId || busy) return;
    setBusy(true);
    setError('');
    try {
      const res = await cancelPendingAction({ actionId });
      setStatus(res?.status || 'cancelled');
      onDecided?.({ actionId, kind: 'email_send', status: res?.status || 'cancelled' });
    } catch (e) {
      setError('Could not cancel. Please try again.');
    } finally {
      setBusy(false);
    }
  }, [actionId, busy, onDecided]);

  if (!actionId) return null;

  const to = recipientLabel(preview.to);
  const cc = recipientLabel(preview.cc);
  const subject = preview.subject || '(no subject)';
  const body = preview.body || '';
  const attachments = attachmentItems(preview);
  const attachmentCount = attachments.length
    ? ` · ${attachments.length} attachment${attachments.length === 1 ? '' : 's'}`
    : '';
  const filesRow = attachments.length > 0 ? (
    <div className="atlas-email-approval-row atlas-email-approval-files">
      <span className="atlas-email-approval-key">{attachments.length === 1 ? 'File' : 'Files'}</span>
      <span className="atlas-email-approval-val"><AttachmentList items={attachments} /></span>
    </div>
  ) : null;
  // The copy promises a file that isn't attached: say so before it goes out.
  const missingAttachment = status === 'pending' && attachments.length === 0
    && ATTACH_MENTION_RE.test(editing ? editBody : body);
  const editDirty = editing && Object.keys(collectEdits().edits || {}).length > 0;
  const fromChoices = Array.isArray(preview.from_choices) ? preview.from_choices.filter(Boolean) : [];
  const canSwitchFrom = status === 'pending' && !preview.from_locked && fromChoices.length > 1;
  const unavailable = unavailableMailboxes(preview);
  const shownFrom = status === 'pending' ? (fromAccount || preview.from) : preview.from;
  const fromRow = preview.from ? (
    <div className="atlas-email-approval-row atlas-email-approval-from">
      <span className="atlas-email-approval-key">From</span>
      <span className="atlas-email-approval-val">
        {canSwitchFrom ? (
          <select
            className="atlas-email-approval-from-select"
            value={fromAccount}
            onChange={(e) => setFromAccount(e.target.value)}
            disabled={busy}
            aria-label="Send from"
          >
            {fromChoices.map((address) => <option key={address} value={address}>{address}</option>)}
          </select>
        ) : (
          shownFrom
        )}
        {canSwitchFrom && preview.from_reason && fromAccount === preview.from && (
          <span className="atlas-email-approval-from-reason">Atlas picked this: {preview.from_reason}</span>
        )}
        {status === 'pending' && preview.from_locked && fromChoices.length === 1 && (
          <span className="atlas-email-approval-from-reason">Replies and forwards go from the mailbox the original is in.</span>
        )}
        {status === 'pending' && unavailable.map((u) => (
          <span key={u.email} className="atlas-email-approval-from-unavailable">
            {u.email} can&apos;t send right now: {u.reason || 'its connection has expired'}.
          </span>
        ))}
      </span>
    </div>
  ) : null;
  const sendAtPassed = Boolean(sendAt) && new Date(sendAt).getTime() < Date.now() + 2 * 60 * 1000;
  const whenRow = status === 'pending' ? (
    <div className="atlas-email-approval-row atlas-email-approval-when">
      <span className="atlas-email-approval-key">When</span>
      <span className="atlas-email-approval-val">
        {sendAt || pickingTime ? (
          <>
            <input
              type="datetime-local"
              className="atlas-email-approval-input atlas-email-approval-when-input"
              value={sendAt}
              onChange={(e) => setSendAt(e.target.value)}
              disabled={busy}
              aria-label="Send at"
            />
            <button type="button" className="atlas-email-approval-link" onClick={() => { setSendAt(''); setPickingTime(false); }} disabled={busy}>
              Send on approval instead
            </button>
            {sendAtPassed && <span className="atlas-email-approval-from-reason">That time has passed or is too close; it will send right away.</span>}
          </>
        ) : (
          <>
            <span>On approval</span>
            <button type="button" className="atlas-email-approval-link" onClick={() => setPickingTime(true)} disabled={busy}>
              Schedule…
            </button>
          </>
        )}
      </span>
    </div>
  ) : null;
  // Forwards/HTML emails carry markup. Render those in a sandboxed iframe (scripts
  // disabled -> XSS-safe) so the preview looks like the actual email instead of raw tags.
  const isHtmlBody = HTML_BODY_RE.test(body);
  // The "editing HTML source" hint must reflect what's currently in the textarea.
  const isEditingHtml = HTML_BODY_RE.test(editBody);
  // Validity of the recipient chips (including any not-yet-committed input text),
  // so we can block Approve on an empty/malformed recipient before the backend 400s.
  const toFinal = editing ? addTokens(editToList, editToText) : [];
  const ccFinal = editing ? addTokens(editCcList, editCcText) : [];
  const recipientsValid =
    !editing || (toFinal.length > 0 && [...toFinal, ...ccFinal].every((e) => EMAIL_RE.test(e)));
  const previewDoc = isHtmlBody
    ? '<!doctype html><html><head><meta charset="utf-8">'
      + '<style>html,body{margin:0}body{font-family:-apple-system,Segoe UI,Roboto,Helvetica,Arial,sans-serif;'
      + 'font-size:13px;line-height:1.45;color:#222;padding:8px;word-wrap:break-word;overflow-wrap:break-word}'
      + 'img{max-width:100%;height:auto}a{color:#0b66c3}'
      + 'blockquote{margin:.5em 0 0;border-left:2px solid #ccc;padding-left:10px;color:#444}</style>'
      + '</head><body>' + body + '</body></html>'
    : '';

  return (
    <div className={`atlas-email-approval atlas-email-approval-${status}`}>
      <div className="atlas-email-approval-head">
        <IoMailOutline size={15} />
        <span className="atlas-email-approval-title">
          {status === 'sent'
            ? 'Email sent'
            : status === 'scheduled'
            ? 'Email scheduled'
            : status === 'unscheduling'
            ? 'Cancelling…'
            : status === 'cancelled'
            ? 'Email cancelled'
            : status === 'superseded'
            ? 'Email replaced'
            : status === 'sending'
            ? 'Sending…'
            : editing
            ? 'Edit before sending'
            : 'Review before sending'}
        </span>
      </div>

      {editing ? (
        <div className="atlas-email-approval-form">
          {fromRow}
          {whenRow}
          <div className="atlas-email-approval-field atlas-email-approval-field-chips">
            <span className="atlas-email-approval-key">To</span>
            <RecipientChips
              value={editToList}
              text={editToText}
              onChange={setEditToList}
              onTextChange={setEditToText}
              placeholder="add email, press Enter"
              disabled={busy}
            />
          </div>
          <div className="atlas-email-approval-field atlas-email-approval-field-chips">
            <span className="atlas-email-approval-key">Cc</span>
            <RecipientChips
              value={editCcList}
              text={editCcText}
              onChange={setEditCcList}
              onTextChange={setEditCcText}
              placeholder="optional"
              disabled={busy}
            />
          </div>
          <label className="atlas-email-approval-field">
            <span className="atlas-email-approval-key">Subject</span>
            <input
              type="text"
              className="atlas-email-approval-input"
              value={editSubject}
              onChange={(e) => setEditSubject(e.target.value)}
              disabled={busy}
            />
          </label>
          {filesRow}
          <textarea
            className="atlas-email-approval-textarea"
            value={editBody}
            onChange={(e) => setEditBody(e.target.value)}
            rows={10}
            disabled={busy}
          />
          {isEditingHtml && (
            <div className="atlas-email-approval-hint">
              This is a forwarded HTML email — you’re editing the HTML source.
            </div>
          )}
        </div>
      ) : (
        <>
          <div className="atlas-email-approval-fields">
            {fromRow}
            {whenRow}
            <div className="atlas-email-approval-row">
              <span className="atlas-email-approval-key">To</span>
              <span className="atlas-email-approval-val">{to || '—'}</span>
            </div>
            {cc && (
              <div className="atlas-email-approval-row">
                <span className="atlas-email-approval-key">Cc</span>
                <span className="atlas-email-approval-val">{cc}</span>
              </div>
            )}
            <div className="atlas-email-approval-row">
              <span className="atlas-email-approval-key">Subject</span>
              <span className="atlas-email-approval-val">{subject}</span>
            </div>
            {filesRow}
          </div>

          {body && (isHtmlBody ? (
            <>
              <iframe
                className="atlas-email-approval-body-html"
                title="Email preview"
                sandbox=""
                srcDoc={previewDoc}
              />
              {preview.body_truncated && (
                <div className="atlas-email-approval-more">…preview truncated; the full email will be sent.</div>
              )}
            </>
          ) : (
            <div className="atlas-email-approval-body">
              {body}
              {preview.body_truncated && <span className="atlas-email-approval-more"> …</span>}
            </div>
          ))}
        </>
      )}

      {missingAttachment && (
        <div className="atlas-email-approval-warn">
          <IoAlertCircleOutline size={15} aria-hidden="true" />
          <span>The message mentions an attachment, but no file is attached. Ask Atlas to attach it before sending.</span>
        </div>
      )}

      {status === 'pending' && !editing && savedNote && (
        <div className="atlas-email-approval-saved">
          <IoCheckmarkCircle size={14} aria-hidden="true" />
          <span>{savedNote}</span>
        </div>
      )}

      {editing && !recipientsValid && (
        <div className="atlas-email-approval-hint">
          {toFinal.length === 0
            ? 'Add at least one recipient to send.'
            : 'One or more addresses are invalid (shown in red).'}
        </div>
      )}

      {status === 'pending' && (
        <div className="atlas-email-approval-actions">
          <button
            type="button"
            className="atlas-email-approval-send"
            onClick={onApprove}
            disabled={busy || !recipientsValid}
          >
            {sendAt && !sendAtPassed ? <IoTimeOutline size={15} /> : <IoSendOutline size={15} />}
            <span>{busy ? (sendAt && !sendAtPassed ? 'Scheduling…' : 'Sending…') : (sendAt && !sendAtPassed ? 'Approve & schedule' : 'Approve & send')}</span>
          </button>
          {editing ? (
            <>
              <button
                type="button"
                className="atlas-email-approval-edit"
                onClick={onSaveEdits}
                disabled={busy || !recipientsValid}
                title="Keep these edits on the card without sending"
              >
                <IoSaveOutline size={15} />
                <span>Save edits</span>
              </button>
              <button type="button" className="atlas-email-approval-cancel" onClick={discardEdit} disabled={busy}>
                <span>Discard edits</span>
              </button>
              {editDirty && <span className="atlas-email-approval-unsaved">Unsaved changes</span>}
            </>
          ) : (
            <>
              <button
                type="button"
                className="atlas-email-approval-edit"
                onClick={enterEdit}
                disabled={busy || !loaded}
                title={loaded ? 'Edit recipient, subject, or message' : 'Loading the full message…'}
              >
                <IoCreateOutline size={15} />
                <span>Edit</span>
              </button>
              <button type="button" className="atlas-email-approval-cancel" onClick={onCancel} disabled={busy}>
                <span>Cancel</span>
              </button>
            </>
          )}
        </div>
      )}

      {status === 'sending' && (
        <div className="atlas-email-approval-status">
          <IoSendOutline size={15} />
          <span>Sending…</span>
        </div>
      )}

      {(status === 'scheduled' || status === 'unscheduling') && (
        <div className="atlas-email-approval-status atlas-email-approval-status-scheduled">
          <IoTimeOutline size={15} />
          <span>
            Scheduled for {formatSendAt(scheduledFor || preview.send_at) || 'the chosen time'}
            {preview.from ? ` from ${preview.from}` : ''} to {to}{attachmentCount}
          </span>
          <button type="button" className="atlas-email-approval-cancel" onClick={onUnschedule} disabled={busy || status === 'unscheduling'}>
            <span>{busy ? 'Cancelling…' : 'Cancel scheduled send'}</span>
          </button>
        </div>
      )}

      {status === 'sent' && (
        <div className="atlas-email-approval-status atlas-email-approval-status-sent">
          <IoCheckmarkCircle size={15} />
          <span>Sent{preview.from ? ` from ${preview.from}` : ''} to {to}{attachmentCount}</span>
        </div>
      )}

      {status === 'cancelled' && (
        <div className="atlas-email-approval-status atlas-email-approval-status-cancelled">
          <IoCloseCircleOutline size={15} />
          <span>{scheduledFor ? 'Unscheduled — nothing was sent' : 'Cancelled — nothing was sent'}</span>
        </div>
      )}

      {status === 'failed' && (
        <div className="atlas-email-approval-status atlas-email-approval-status-cancelled">
          <IoCloseCircleOutline size={15} />
          <span>Send failed — nothing was sent</span>
        </div>
      )}

      {status === 'superseded' && (
        <div className="atlas-email-approval-status atlas-email-approval-status-cancelled">
          <IoCloseCircleOutline size={15} />
          <span>Replaced by a newer draft — no longer sendable</span>
        </div>
      )}

      {status === 'pending' && !error && preview.send_error && (
        <div className="atlas-email-approval-error">Last try didn&apos;t send: {preview.send_error}</div>
      )}
      {error && <div className="atlas-email-approval-error">{error}</div>}
    </div>
  );
}
